<script lang="ts" setup>
import { ref, watch, computed, onUnmounted } from 'vue'
import { useTheme } from 'vuetify'
import { useI18n } from 'vue-i18n'
import { useStripeElements, confirmSetupWithElements } from '@/composables/useStripe'
import type { StripePaymentElement } from '@stripe/stripe-js'

defineOptions({ name: 'StripeSetupModal' })

const { t } = useI18n()

const props = defineProps<{
  modelValue: boolean
  clientSecret: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'success', paymentMethodId: string): void
  (e: 'error', message: string): void
}>()

const theme = useTheme()
const isDark = computed(() => theme.global.current.value.dark)

const clientSecretRef = ref<string | null>(props.clientSecret)
const {
  elements,
  loading: stripeLoading,
  error: stripeError,
  initialize
} = useStripeElements(clientSecretRef)

const paymentElementRef = ref<HTMLDivElement | null>(null)
const paymentElement = ref<StripePaymentElement | null>(null)
const processing = ref(false)
const errorMessage = ref<string | null>(null)
const paymentElementReady = ref(false)

// Initialize Stripe when modal opens
watch(
  () => props.modelValue,
  async (isOpen) => {
    if (isOpen && props.clientSecret) {
      clientSecretRef.value = props.clientSecret
      errorMessage.value = null
      paymentElementReady.value = false
      await initialize(isDark.value)
      mountPaymentElement()
    }
  },
  { immediate: true }
)

// Re-mount element when theme changes
watch(isDark, async () => {
  if (props.modelValue && elements.value) {
    clientSecretRef.value = props.clientSecret
    await initialize(isDark.value)
    mountPaymentElement()
  }
})

function mountPaymentElement() {
  if (!elements.value || !paymentElementRef.value) return

  if (paymentElement.value) {
    paymentElement.value.destroy()
  }

  paymentElement.value = elements.value.create('payment', {
    layout: 'tabs'
  })

  paymentElement.value.on('ready', () => {
    paymentElementReady.value = true
  })

  paymentElement.value.on('change', (event) => {
    if (event.complete) {
      errorMessage.value = null
    }
  })

  paymentElement.value.mount(paymentElementRef.value)
}

async function handleSubmit() {
  if (!elements.value || processing.value) return

  processing.value = true
  errorMessage.value = null

  try {
    const { error: submitError } = await elements.value.submit()
    if (submitError) {
      errorMessage.value = submitError.message || t('dashboard.account.payments.validationError')
      return
    }

    const returnUrl = `${window.location.origin}/dashboard/account/payments`
    const result = await confirmSetupWithElements(elements.value, returnUrl)

    if (!result.success || !result.paymentMethodId) {
      errorMessage.value = result.error || t('dashboard.account.payments.setupFailed')
      emit('error', errorMessage.value)
    } else {
      emit('success', result.paymentMethodId)
    }
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : t('dashboard.account.payments.unknownError')
    emit('error', errorMessage.value)
  } finally {
    processing.value = false
  }
}

function handleClose() {
  if (!processing.value) {
    emit('update:modelValue', false)
  }
}

onUnmounted(() => {
  if (paymentElement.value) {
    paymentElement.value.destroy()
  }
})
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="500"
    scrollable
    persistent
    @update:model-value="handleClose"
  >
    <v-card class="setup-modal">
      <v-card-title class="modal-header">
        <div class="header-content">
          <v-icon icon="mdi-credit-card-plus-outline" size="24" class="header-icon" />
          <span>{{ $t('dashboard.account.payments.addMethod') }}</span>
        </div>
        <v-btn icon variant="text" size="small" :disabled="processing" @click="handleClose">
          <v-icon icon="mdi-close" />
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="modal-body">
        <div v-if="stripeLoading" class="loading-state">
          <v-progress-circular indeterminate size="32" width="2" color="primary" />
          <span>{{ $t('dashboard.account.payments.loadingStripe') }}</span>
        </div>

        <v-alert v-if="stripeError" type="error" variant="tonal" class="mb-4">
          {{ stripeError }}
        </v-alert>

        <div
          v-show="!stripeLoading && !stripeError"
          ref="paymentElementRef"
          class="payment-element-container"
        />

        <v-alert v-if="errorMessage" type="error" variant="tonal" class="mt-4" density="compact">
          {{ errorMessage }}
        </v-alert>

        <div class="security-note">
          <v-icon icon="mdi-lock-outline" size="14" />
          <span>{{ $t('dashboard.account.payments.securityNote') }}</span>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="modal-actions">
        <v-btn variant="text" :disabled="processing" @click="handleClose">
          {{ $t('common.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          :loading="processing"
          :disabled="stripeLoading || !!stripeError || !paymentElementReady"
          @click="handleSubmit"
        >
          <v-icon icon="mdi-check" class="me-2" size="18" />
          {{ $t('dashboard.account.payments.add') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style lang="scss" scoped>
.setup-modal {
  border-radius: 16px !important;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px !important;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.1rem;
  font-weight: 600;
}

.header-icon {
  color: var(--token-color-primary, #1c6ee3);
}

.modal-body {
  padding: 24px 20px !important;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 40px 20px;
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.9rem;
}

.payment-element-container {
  min-height: 200px;
}

.security-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

.modal-actions {
  padding: 12px 20px !important;
}

/* Light Theme */
.v-theme--lightTheme {
  .loading-state {
    color: rgba(15, 23, 42, 0.6);
  }

  .security-note {
    background: rgba(0, 0, 0, 0.03);
    color: rgba(15, 23, 42, 0.5);
  }
}
</style>
